/** useQsrTasks — persisted assigned-task state for the QSR demo. */
import { useCallback, useEffect, useState } from 'react';
import { loadTasks, saveTasks, seedBacklog, computeDue } from './qsrTasks';

export default function useQsrTasks(businessId, enabled = true) {
  const [tasks, setTasks] = useState(() => {
    if (!enabled) return [];
    const existing = loadTasks(businessId);
    return existing || seedBacklog();
  });

  useEffect(() => { if (enabled) saveTasks(businessId, tasks); }, [businessId, tasks, enabled]);

  // Add a task from a Corvana AI action; ignore if one is already open for it.
  const add = useCallback((a) => {
    setTasks((prev) => {
      if (prev.some((t) => t.actionId === a.actionId && t.status !== 'done')) return prev;
      const task = {
        id: `qt_${Date.now()}_${a.actionId}`,
        actionId: a.actionId,
        title: a.title,
        owner: a.owner,
        store: a.store,
        priority: a.priority || 'medium',
        due: a.due || computeDue(a.priority),
        status: 'open',
        createdAt: new Date().toISOString(),
      };
      return [task, ...prev];
    });
  }, []);

  const toggle = useCallback((id) => {
    setTasks((prev) => prev.map((t) => t.id === id
      ? { ...t, status: t.status === 'done' ? 'open' : 'done', doneAt: t.status === 'done' ? null : new Date().toISOString() }
      : t));
  }, []);

  const remove = useCallback((id) => setTasks((prev) => prev.filter((t) => t.id !== id)), []);

  const hasOpen = useCallback((actionId) => tasks.some((t) => t.actionId === actionId && t.status !== 'done'), [tasks]);

  return { tasks, add, toggle, remove, hasOpen };
}
